/**
 * Cache Service Implementations
 * 
 * Provides caching functionality with TTL support,
 * memory management, and configurable eviction policies.
 * 
 * @author Ertugrul Kücükkaya
 * @version 1.0.0
 */

import type { ICacheService, ServiceResult } from '../interfaces/IDataService';
import type { ILogger } from '../logging/Logger';

/**
 * Cache entry with TTL support
 */
interface CacheEntry<T> {
  value: T;
  expiresAt: number;
  accessCount: number;
  lastAccessed: number;
}

/**
 * Cache statistics
 */
interface CacheStats {
  hits: number;
  misses: number;
  sets: number;
  deletes: number;
  evictions: number;
  size: number;
  hitRate: number;
}

/**
 * Cache configuration
 */
interface CacheConfig {
  maxSize: number;
  defaultTTL: number;
  cleanupInterval: number;
  evictionPolicy: 'LRU' | 'LFU' | 'TTL';
  [key: string]: unknown; // Allow additional properties for logging metadata
}

/**
 * Memory-based cache service implementation
 */
export class MemoryCacheService implements ICacheService {
  private readonly cache = new Map<string, CacheEntry<unknown>>();
  private readonly logger: ILogger;
  private readonly config: CacheConfig;
  private cleanupTimer?: NodeJS.Timeout;
  private stats: CacheStats;

  constructor(
    logger: ILogger,
    defaultTTL = 300000, // 5 minutes
    maxSize = 1000,
    cleanupInterval = 60000 // 1 minute
  ) {
    this.logger = logger.child('MemoryCacheService');
    this.config = {
      maxSize,
      defaultTTL,
      cleanupInterval,
      evictionPolicy: 'LRU',
    };

    this.stats = {
      hits: 0,
      misses: 0,
      sets: 0,
      deletes: 0,
      evictions: 0,
      size: 0,
      hitRate: 0,
    };

    this.startCleanupTimer();
    this.logger.info('Memory cache service initialized', 'constructor', this.config);
  }

  /**
   * Get cached value
   */
  public async get<T>(key: string): Promise<ServiceResult<T>> {
    try {
      this.validateKey(key);

      const entry = this.cache.get(key);
      const now = Date.now();

      if (!entry) {
        this.stats.misses++;
        this.updateHitRate();
        this.logger.debug('Cache miss', 'get', { key });
        
        return {
          success: false,
          error: 'Cache miss',
        };
      }

      // Check if expired
      if (entry.expiresAt <= now) {
        this.cache.delete(key);
        this.stats.misses++;
        this.stats.evictions++;
        this.updateHitRate();
        this.logger.debug('Cache entry expired', 'get', { key });
        
        return {
          success: false,
          error: 'Cache entry expired',
        };
      }

      // Update access information
      entry.accessCount++;
      entry.lastAccessed = now;

      this.stats.hits++;
      this.updateHitRate();
      this.logger.debug('Cache hit', 'get', { key });

      return {
        success: true,
        data: entry.value as T,
      };

    } catch (error) {
      this.logger.error('Failed to get cache entry', 'get', { key }, error as Error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown cache error',
      };
    }
  }

  /**
   * Set cached value
   */
  public async set<T>(key: string, value: T, ttl?: number): Promise<ServiceResult<void>> {
    try {
      this.validateKey(key);
      this.validateValue(value);

      const now = Date.now();
      const effectiveTTL = ttl || this.config.defaultTTL;
      const expiresAt = now + effectiveTTL;

      // Check if we need to evict entries
      if (this.cache.size >= this.config.maxSize && !this.cache.has(key)) {
        await this.evictEntries(1);
      }

      const entry: CacheEntry<T> = {
        value,
        expiresAt,
        accessCount: 0,
        lastAccessed: now,
      };

      this.cache.set(key, entry);
      this.stats.sets++;
      this.stats.size = this.cache.size;

      this.logger.debug('Cache entry set', 'set', { 
        key, 
        ttl: effectiveTTL, 
        expiresAt: new Date(expiresAt).toISOString() 
      });

      return {
        success: true,
      };

    } catch (error) {
      this.logger.error('Failed to set cache entry', 'set', { key, ttl }, error as Error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown cache error',
      };
    }
  }

  /**
   * Delete cached value
   */
  public async delete(key: string): Promise<ServiceResult<boolean>> {
    try {
      this.validateKey(key);

      const deleted = this.cache.delete(key);
      
      if (deleted) {
        this.stats.deletes++;
        this.stats.size = this.cache.size;
        this.logger.debug('Cache entry deleted', 'delete', { key });
      } else {
        this.logger.debug('Cache entry not found for deletion', 'delete', { key });
      }

      return {
        success: true,
        data: deleted,
      };

    } catch (error) {
      this.logger.error('Failed to delete cache entry', 'delete', { key }, error as Error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown cache error',
      };
    }
  }

  /**
   * Clear all cache
   */
  public async clear(): Promise<ServiceResult<void>> {
    try {
      const previousSize = this.cache.size;
      this.cache.clear();
      
      this.stats.size = 0;
      this.stats.evictions += previousSize;

      this.logger.info('Cache cleared', 'clear', { previousSize });

      return {
        success: true,
      };

    } catch (error) {
      this.logger.error('Failed to clear cache', 'clear', {}, error as Error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown cache error',
      };
    }
  }

  /**
   * Check if key exists
   */
  public async exists(key: string): Promise<ServiceResult<boolean>> {
    try {
      this.validateKey(key);

      const entry = this.cache.get(key);
      const now = Date.now();

      if (!entry) {
        return {
          success: true,
          data: false,
        };
      }

      // Check if expired
      if (entry.expiresAt <= now) {
        this.cache.delete(key);
        this.stats.evictions++;
        
        return {
          success: true,
          data: false,
        };
      }

      return {
        success: true,
        data: true,
      };

    } catch (error) {
      this.logger.error('Failed to check cache key existence', 'exists', { key }, error as Error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown cache error',
      };
    }
  }

  /**
   * Get cache statistics
   */
  public getStats(): CacheStats {
    this.stats.size = this.cache.size;
    return { ...this.stats };
  }

  /**
   * Get cache configuration
   */
  public getConfig(): Readonly<CacheConfig> {
    return { ...this.config };
  }

  /**
   * Get all keys (for debugging)
   */
  public getKeys(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * Manually trigger cleanup
   */
  public async cleanup(): Promise<void> {
    const before = this.cache.size;
    const now = Date.now();
    let cleaned = 0;

    for (const [key, entry] of this.cache.entries()) {
      if (entry.expiresAt <= now) {
        this.cache.delete(key);
        cleaned++;
      }
    }

    if (cleaned > 0) {
      this.stats.evictions += cleaned;
      this.stats.size = this.cache.size;
      this.logger.debug('Cache cleanup completed', 'cleanup', { 
        before, 
        after: this.cache.size, 
        cleaned 
      });
    }
  }

  /**
   * Destroy the cache service
   */
  public destroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = undefined;
    }
    
    this.cache.clear();
    this.logger.info('Memory cache service destroyed', 'destroy');
  }

  /**
   * Private helper methods
   */
  private startCleanupTimer(): void {
    this.cleanupTimer = setInterval(() => {
      this.cleanup().catch(error => {
        this.logger.error('Cache cleanup failed', 'startCleanupTimer', {}, error as Error);
      });
    }, this.config.cleanupInterval);
  }

  private async evictEntries(count: number): Promise<void> {
    if (this.cache.size === 0) {return;}

    const entriesToEvict = Array.from(this.cache.entries())
      .sort((a, b) => {
        switch (this.config.evictionPolicy) {
          case 'LRU':
            return a[1].lastAccessed - b[1].lastAccessed;
          case 'LFU':
            return a[1].accessCount - b[1].accessCount;
          case 'TTL':
            return a[1].expiresAt - b[1].expiresAt;
          default:
            return a[1].lastAccessed - b[1].lastAccessed;
        }
      })
      .slice(0, count);

    for (const [key] of entriesToEvict) {
      this.cache.delete(key);
      this.stats.evictions++;
    }

    this.stats.size = this.cache.size;
    this.logger.debug('Cache entries evicted', 'evictEntries', { 
      count: entriesToEvict.length, 
      policy: this.config.evictionPolicy 
    });
  }

  private updateHitRate(): void {
    const total = this.stats.hits + this.stats.misses;
    this.stats.hitRate = total > 0 ? this.stats.hits / total : 0;
  }

  private validateKey(key: string): void {
    if (!key || typeof key !== 'string' || key.trim().length === 0) {
      throw new Error('Invalid cache key: must be a non-empty string');
    }

    if (key.length > 250) {
      throw new Error('Invalid cache key: too long (max 250 characters)');
    }
  }

  private validateValue(value: unknown): void {
    if (value === undefined) {
      throw new Error('Invalid cache value: cannot cache undefined');
    }

    // Check for circular references
    try {
      JSON.stringify(value);
    } catch {
      throw new Error('Invalid cache value: contains circular references or is not serializable');
    }
  }
} 